import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { callGetInstallerUrl, type InstallerPlatform } from '../functions'
import { useAuth } from '../auth/AuthProvider'
import { isAdminUid } from '../admin/adminAllowlist'

interface PlatformOption {
  id: InstallerPlatform
  label: string
  hint: string
}

// Windows-only for the initial beta. Mac and Linux are commented out and
// can be re-enabled once those builds are cut and the installers are
// uploaded to Storage under releases/<platform>/<version>/.
const PLATFORMS: PlatformOption[] = [
  { id: 'win', label: 'Windows', hint: 'Windows 10/11 · .exe installer' },
  // { id: 'mac', label: 'macOS', hint: 'macOS 11+ · .dmg (unsigned)' },
  // { id: 'linux', label: 'Linux', hint: 'AppImage' },
]

export function DownloadScreen() {
  const { isBeta, user, loading } = useAuth()
  const [busyPlatform, setBusyPlatform] = useState<InstallerPlatform | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastDownload, setLastDownload] = useState<{ platform: string; version: string } | null>(null)

  // Strict gate: anonymous visitors never see the download screen, they get
  // bounced to sign-in. Non-beta users (signed in but no custom claim) get
  // bounced to the landing page — there is no path to the installer that
  // doesn't go through a valid beta account.
  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center text-text-tertiary">Loading…</div>
    )
  }
  if (!user) {
    return <Navigate to="/signin" replace />
  }
  // Admins get the same access as beta-claim holders — mirrors the
  // server-side requireBeta check in functions/src/lib/auth.ts.
  if (!isBeta && !isAdminUid(user.uid)) {
    return <Navigate to="/" replace />
  }

  const handleDownload = async (platform: InstallerPlatform) => {
    setBusyPlatform(platform)
    setError(null)
    try {
      const res = await callGetInstallerUrl({ platform })
      const { url, version, fileName } = res.data
      // Kick off the download. Using an anchor + click lets the browser
      // pick up the signed URL's Content-Disposition if set, and falls
      // back to the suggested filename otherwise.
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setLastDownload({ platform, version })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Download failed.')
    } finally {
      setBusyPlatform(null)
    }
  }

  return (
    <div className="min-h-dvh px-6 py-10 max-w-xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-semibold">Download Govu</h1>
        <Link to="/" className="text-sm text-text-secondary hover:text-text-primary">
          ← Back
        </Link>
      </div>

      <p className="text-text-secondary mb-6">
        Pick your platform. The download link is single-use and expires in 10 minutes.
      </p>

      <div className="space-y-3">
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => handleDownload(p.id)}
            disabled={busyPlatform !== null}
            className="w-full flex items-center justify-between p-4 bg-bg-secondary hover:bg-bg-tertiary border border-border-primary rounded-lg text-left disabled:opacity-50"
          >
            <div>
              <div className="font-medium">{p.label}</div>
              <div className="text-xs text-text-tertiary mt-0.5">{p.hint}</div>
            </div>
            <div className="text-accent-primary text-sm font-medium">
              {busyPlatform === p.id ? 'Preparing…' : 'Download'}
            </div>
          </button>
        ))}
      </div>

      {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
      {lastDownload && (
        <p className="mt-4 text-sm text-text-tertiary">
          Downloading Govu {lastDownload.version} for {lastDownload.platform}…
        </p>
      )}

      <p className="mt-10 text-xs text-text-tertiary">
        Once installed, the app updates itself automatically — you won't need to come back here for patches.
      </p>
    </div>
  )
}
